import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Olá! Bem-vindo à casa in gold by Fhiama Fonseca. Como posso ajudá-lo hoje?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickReplies = [
    { id: 1, text: 'Ver Catálogo', action: 'catalog' },
    { id: 2, text: 'Horário de Atendimento', action: 'hours' },
    { id: 3, text: 'Solicitar Orçamento', action: 'quote' },
    { id: 4, text: 'Falar com Consultor', action: 'whatsapp' },
  ];

  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();

    if (msg.includes('catálogo') || msg.includes('catalogo') || msg.includes('produto')) {
      return {
        text: 'Temos uma coleção exclusiva de móveis residenciais e corporativos. Posso direcioná-lo ao nosso catálogo completo!',
        action: 'catalog',
      };
    }

    if (msg.includes('preço') || msg.includes('preco') || msg.includes('valor') || msg.includes('custo')) {
      return {
        text: 'Nossos móveis são feitos sob medida com materiais premium. Para um orçamento personalizado, por favor visite nossa página de contato ou fale diretamente com nosso consultor via WhatsApp.',
        action: null,
      };
    }

    if (msg.includes('horário') || msg.includes('horario') || msg.includes('atendimento') || msg.includes('aberto')) {
      return {
        text: 'Nosso atendimento funciona:\n\nSegunda a Sexta: 9h às 18h\nSábado: 10h às 14h\nDomingo: Fechado\n\nPara atendimento imediato, fale conosco pelo WhatsApp!',
        action: null,
      };
    }

    if (msg.includes('entrega') || msg.includes('prazo') || msg.includes('frete')) {
      return {
        text: 'Trabalhamos com entrega para todo Brasil. O prazo varia de acordo com a localização e personalização do produto (15-45 dias). Entre em contato para calcularmos o frete para sua região.',
        action: null,
      };
    }

    if (msg.includes('contato') || msg.includes('telefone') || msg.includes('email') || msg.includes('whatsapp')) {
      return {
        text: 'Você pode nos contatar:\n\n📧 Email: contato@casaingold.com.br\n📱 WhatsApp: (11) 3456-7890\n📞 Telefone: (11) 3456-7890\n\nClique abaixo para falar diretamente no WhatsApp!',
        action: 'whatsapp',
      };
    }

    if (msg.includes('orçamento') || msg.includes('orcamento') || msg.includes('pedido')) {
      return {
        text: 'Ficarei feliz em ajudar com seu orçamento! Você pode preencher nosso formulário online ou falar diretamente com nosso consultor via WhatsApp.',
        action: 'quote',
      };
    }

    return {
      text: 'Obrigado pela sua mensagem! Para melhor atendê-lo, escolha uma das opções abaixo ou fale diretamente com nosso consultor especializado via WhatsApp.',
      action: null,
    };
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');

    setTimeout(() => {
      const botResponse = getBotResponse(inputMessage);
      const botMessage = {
        id: messages.length + 2,
        text: botResponse.text,
        sender: 'bot',
        timestamp: new Date(),
        action: botResponse.action,
      };
      setMessages((prev) => [...prev, botMessage]);
    }, 800);
  };

  const handleQuickReply = (action) => {
    let response = '';
    let nextAction = null;

    switch (action) {
      case 'catalog':
        response = 'Perfeito! Redirecionando você para nosso catálogo...';
        nextAction = () => {
          navigate('/catalogo');
          setIsOpen(false);
        };
        break;
      case 'hours':
        response =
          'Nosso atendimento funciona:\n\nSegunda a Sexta: 9h às 18h\nSábado: 10h às 14h\nDomingo: Fechado\n\nPara atendimento imediato, fale conosco pelo WhatsApp!';
        break;
      case 'quote':
        response = 'Vou direcioná-lo para nosso formulário de orçamento...';
        nextAction = () => {
          navigate('/contato');
          setIsOpen(false);
        };
        break;
      case 'whatsapp':
        response = 'Abrindo WhatsApp para você falar com nosso consultor especializado...';
        nextAction = () => {
          window.open(
            'https://wa.me/5588996762222?text=Olá! Vim do site e gostaria de falar sobre móveis de luxo.',
            '_blank'
          );
        };
        break;
      default:
        response = 'Como posso ajudá-lo?';
    }

    const botMessage = {
      id: messages.length + 1,
      text: response,
      sender: 'bot',
      timestamp: new Date(),
    };

    setMessages([...messages, botMessage]);

    if (nextAction) {
      setTimeout(nextAction, 1000);
    }
  };

  const handleAction = (action) => {
    switch (action) {
      case 'catalog':
        navigate('/catalogo');
        setIsOpen(false);
        break;
      case 'quote':
        navigate('/contato');
        setIsOpen(false);
        break;
      case 'whatsapp':
        window.open(
          'https://wa.me/5511934567890?text=Olá! Vim do site e gostaria de falar sobre móveis de luxo.',
          '_blank'
        );
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chatbot-open-button"
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-lg hover:bg-black transition-all duration-500 hover:scale-110"
          aria-label="Abrir chat"
        >
          <MessageCircle className="w-7 h-7" strokeWidth={1.5} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-96 h-[600px] bg-white border border-border shadow-2xl flex flex-col"
          style={{ maxHeight: 'calc(100vh - 48px)' }}
        >
          {/* Header */}
          <div className="bg-primary text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-accent text-xs uppercase tracking-widest">Atendimento</h3>
              <p className="text-xs text-white/70 mt-1">casa in gold</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close-button"
              className="hover:bg-white/10 p-2 rounded transition-colors"
              aria-label="Fechar chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-secondary/30">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 ${
                    message.sender === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-white border border-border text-foreground'
                  }`}
                >
                  <p className="text-sm font-light leading-relaxed whitespace-pre-line">{message.text}</p>
                  {message.action && (
                    <button
                      onClick={() => handleAction(message.action)}
                      className="mt-3 text-xs uppercase tracking-widest underline hover:no-underline transition-all"
                    >
                      {message.action === 'catalog' && 'Ver Catálogo →'}
                      {message.action === 'quote' && 'Solicitar Orçamento →'}
                      {message.action === 'whatsapp' && 'Abrir WhatsApp →'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="p-4 border-t border-border bg-white">
              <p className="text-xs text-muted-foreground mb-3 font-accent uppercase tracking-widest">
                Respostas Rápidas:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {quickReplies.map((reply) => (
                  <button
                    key={reply.id}
                    onClick={() => handleQuickReply(reply.action)}
                    data-testid={`quick-reply-${reply.action}`}
                    className="text-xs border border-border px-3 py-2 hover:bg-primary hover:text-white hover:border-primary transition-all duration-300"
                  >
                    {reply.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Digite sua mensagem..."
                data-testid="chatbot-input"
                className="flex-1 bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm focus:ring-0 outline-none transition-colors duration-300"
              />
              <button
                onClick={handleSendMessage}
                data-testid="chatbot-send-button"
                disabled={!inputMessage.trim()}
                className="bg-primary text-white w-10 h-10 flex items-center justify-center hover:bg-black transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Enviar mensagem"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;